import { useRef, useEffect, useState, useMemo } from "react";
import { calculateOffScreenPosition } from "../Utils/cursorCalculations";

const convertHexToRGBA = (hexCode, opacity = 1) => {
  if (!hexCode) {
    return "transparent";
  }
  let hex = hexCode.replace('#', '');
  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  if (opacity > 1 && opacity <= 100) {
    opacity = opacity / 100;
  }
  return `rgba(${r},${g},${b},${opacity})`;
};

// ✅ FIX: Added the 'export' keyword to make this object available to other files.
export const CURSOR_COLORS = {
  yellow: { default: "#FFBB00", dim: "#503F10" },
  red: { default: "#FF3366", dim: "#501D2A" },
  blue: { default: "#0088FF", dim: "#103250" },
  green: { default: "#22DD88", dim: "#194832" },
  orange: { default: "#FF8800", dim: "#503210" },
  pink: { default: "#FF0099", dim: "#501037" },
  purple: { default: "#AA44FF", dim: "#3B2150" },
};

export default function Cursor({ x, y, name, color, profilePicture, lastActive }) {
  const rCursor = useRef(null);
  const [dimensions, setDimensions] = useState(null);
  const [isOffScreen, setIsOffScreen] = useState(false);

  const CURSOR_SIZE = 35;
  
  // Manual offset variables for fine-tuning arrow positioning
  const ARROW_DISTANCE = 6; // Distance from circle edge
  const ARROW_ROTATION_OFFSET = 225; // Rotation offset for arrow direction
  
  // Individual edge margins to keep arrow visible
  const LEFT_EDGE_MARGIN = 0; // Margin from left edge
  const RIGHT_EDGE_MARGIN = 50; // Margin from right edge
  const TOP_EDGE_MARGIN = 0; // Margin from top edge
  const BOTTOM_EDGE_MARGIN = 0; // Margin from bottom edge
  
  // Cursor positioning offsets
  const CURSOR_LEFT_OFFSET = 170; // Left offset for cursor positioning (resource column width)
  const CURSOR_TOP_OFFSET = 15; // Top offset for cursor positioning (header height)
  
  // Boundary margins to exclude header areas (now handled in cursorCalculations.js)
  // const TOP_BOUNDARY_MARGIN = 0; // Margin to lower the main boundary past the date/week header

  // Calculate time since last activity
  const now = new Date();
  const lastActiveTime = lastActive ? new Date(lastActive) : now;
  const minutesSinceLastActive = (now.getTime() - lastActiveTime.getTime()) / (1000 * 60);
  
  // Apply grey styling if user has been away for more than 1 minute
  const isAway = minutesSinceLastActive > 1;
  
  // Use grey colors when away, original colors when active
  const displayColor = isAway ? {
    default: '#808080', // Grey border
    dim: '#404040'      // Darker grey background
  } : color;

  // Only update dimensions on mount and resize
  useEffect(() => {
    const updateDimensions = () => {
      const container = document.querySelector('.mbsc-timeline-grid-scroll');
      const resourceColumn = document.querySelector('.mbsc-timeline-resource-col');
      
      if (container && resourceColumn) {
        setDimensions({
          width: container.clientWidth,
          height: container.clientHeight,
          resourceColumnWidth: resourceColumn.clientWidth
        });
      }
    };
    
    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    const container = document.querySelector('.mbsc-timeline-grid-scroll');
    if (container) {
      resizeObserver.observe(container);
    }
    
    return () => resizeObserver.disconnect();
  }, []);

  // Memoize position calculations
  const { position, fixedPosition } = useMemo(() => {
    if (!dimensions) return { position: { isOffScreen: false }, fixedPosition: { x: 0, y: 0 } };

    const pos = calculateOffScreenPosition(x, y, dimensions.width, dimensions.height, dimensions.resourceColumnWidth);
    
    let fixed = { x: pos.x, y: pos.y };
    if (pos.isOffScreen) {
      switch(pos.angle) {
        case 0: fixed = { x: x, y: pos.y }; break;
        case 90: fixed = { x: pos.x, y: y }; break;
        case 180: fixed = { x: x, y: pos.y }; break;
        case 270: fixed = { x: pos.x, y: y }; break;
        default: break;
      }
    }

    return { position: pos, fixedPosition: fixed };
  }, [x, y, dimensions]);

  // Handle visibility changes
  useEffect(() => {
    setIsOffScreen(position.isOffScreen);
  }, [position.isOffScreen]);

  if (!color || !color.default || !color.dim || !name) {
    return null;
  }

  // Helper function to calculate the arrow position around the circle based on cursor direction
  const calculateArrowPosition = (indicatorPos, cursorPos) => {
    // Position the arrow around the circle based on the direction to cursor
    const circleRadius = CURSOR_SIZE / 2; // 17.5px
    const arrowDistance = ARROW_DISTANCE; // Distance from circle edge
    
    // Calculate the actual direction from indicator to cursor
    const deltaX = cursorPos.x - indicatorPos.x;
    const deltaY = cursorPos.y - indicatorPos.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // If cursor is very close, use a default direction
    if (distance < 1) {
      return {
        rotation: 0,
        x: circleRadius + arrowDistance,
        y: circleRadius + arrowDistance
      };
    }
    
    // Calculate the angle from indicator to cursor
    const angleToCursor = Math.atan2(deltaY, deltaX);
    
    // Position the arrow around the circle at the calculated angle
    const positionX = Math.cos(angleToCursor) * (circleRadius + arrowDistance);
    const positionY = Math.sin(angleToCursor) * (circleRadius + arrowDistance);
    
    // The arrow SVG points down-left by default, so we need to adjust for that
    const actualAngle = angleToCursor * (180 / Math.PI) + ARROW_ROTATION_OFFSET;
    
    return { 
      rotation: actualAngle, 
      x: positionX, 
      y: positionY 
    };
  };


  // Calculate positions for both on-screen and off-screen states
  const isLeftOffScreen = position.angle === 270 || position.angle === 225 || position.angle === 315;
  const isTopOffScreen = position.angle === 0 || position.angle === 45 || position.angle === 315;
  
  // Calculate arrow position for both on-screen and off-screen states
  let arrowPos = null;
  if (dimensions) {
    if (isOffScreen) {
      // Off-screen: arrow points towards actual cursor position
      const indicatorPos = { 
        x: fixedPosition.x + (isLeftOffScreen ? 0 : CURSOR_LEFT_OFFSET), 
        y: fixedPosition.y + (isTopOffScreen ? 0 : CURSOR_TOP_OFFSET) 
      };
      
      const actualCursorPos = { 
        x: x + (isLeftOffScreen ? 0 : CURSOR_LEFT_OFFSET), 
        y: y + (isTopOffScreen ? 0 : CURSOR_TOP_OFFSET) 
      };
      
      arrowPos = calculateArrowPosition(indicatorPos, actualCursorPos);
    } else {
      // On-screen: arrow points down-left as home position
      const circleRadius = CURSOR_SIZE / 2;
      const arrowDistance = ARROW_DISTANCE;
      
      // Position arrow at bottom-left 
      // In CSS coordinates: 0° = right, 90° = down, 180° = left, 270° = up
      // Bottom-left should be at 135° (180° - 45°)
      const angle135 = (135 * Math.PI) / 180; // Convert to radians
      const positionX = Math.cos(angle135) * (circleRadius + arrowDistance);
      const positionY = Math.sin(angle135) * (circleRadius + arrowDistance);
      
      arrowPos = {
        rotation: 0, // Arrow points in the direction of 225° position
        x: positionX,
        y: positionY
      };
    }
  }

  // Calculate final position
  let finalX, finalY;
  if (isOffScreen && dimensions) {
    // Off-screen positioning
    finalX = fixedPosition.x + (isLeftOffScreen ? 0 : CURSOR_LEFT_OFFSET);
    finalY = fixedPosition.y + (isTopOffScreen ? 0 : CURSOR_TOP_OFFSET);
    
    // Adjust X position to keep arrow visible
    if (position.angle === 90 || position.angle === 135 || position.angle === 45) {
      finalX = Math.min(finalX, dimensions.width - RIGHT_EDGE_MARGIN);
    } else if (position.angle === 270 || position.angle === 225 || position.angle === 315) {
      finalX = Math.max(finalX, LEFT_EDGE_MARGIN);
    }
    
    // Adjust Y position to keep arrow visible
    if (position.angle === 0 || position.angle === 45 || position.angle === 315) {
      finalY = Math.max(finalY, TOP_EDGE_MARGIN);
    } else if (position.angle === 180 || position.angle === 135 || position.angle === 225) {
      finalY = Math.min(finalY, dimensions.height - BOTTOM_EDGE_MARGIN);
    }
  } else {
    // On-screen positioning
    finalX = x + CURSOR_LEFT_OFFSET;
    finalY = y + CURSOR_TOP_OFFSET;
  }

  // Render single morphing cursor element
  return (
    <div
      ref={rCursor}
      className={isOffScreen ? "cursor-indicator" : "cursor-image"}
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        transform: `translate3d(${finalX}px, ${finalY}px, 0)`,
        opacity: 1,
        zIndex: isOffScreen ? 1000 : 4,
        willChange: 'opacity, transform',
        pointerEvents: 'none',
        transition: isOffScreen ? 'opacity 0.3s ease-out, transform 0.15s ease-out' : 'transform 120ms linear'
      }}
    >
      <div
        style={{
          width: CURSOR_SIZE,
          height: CURSOR_SIZE,
          position: 'absolute',
          fontSize: 17,
          userSelect: 'none',
          backgroundColor: convertHexToRGBA(displayColor?.dim, 0.5),
          boxShadow: `inset 0px 0px 0px 2px ${displayColor?.default || '#808080'}, 0px 8px 16px rgba(0,0,0,0.4)`,
          borderRadius: '50%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundImage: profilePicture ? `url(${profilePicture})` : "",
          backgroundSize: "contain",
        }}
      >
        <p style={{ margin: 0, color: isAway ? '#FFFFFF' : (displayColor?.default || '#808080'), fontWeight: 600 }}>{name.charAt(0).toUpperCase()}</p>
        <svg
          style={arrowPos ? {
            // Smooth arrow positioning for both on-screen and off-screen
            position: 'absolute',
            left: `calc(50% + ${arrowPos.x}px)`,
            top: `calc(50% + ${arrowPos.y}px)`,
            transform: `translate(-50%, -50%) rotate(${arrowPos.rotation}deg)`,
            transformOrigin: 'center',
            transition: 'all 0.15s ease-out'
          } : {
            // Fallback positioning (should not be used with new logic)
            position: 'absolute',
            bottom: -2,
            left: -2
          }}
          width="10"
          height="10"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2 12H9.17157C10.9534 12 11.8457 9.84572 10.5858 8.58579L3.41421 1.41421C2.15428 0.154281 0 1.04662 0 2.82843V10C0 11.1046 0.89543 12 2 12Z"
            fill={convertHexToRGBA(displayColor?.default, 1)}
          />
        </svg>
      </div>
    </div>
  );
}
